import { Auth } from "aws-amplify";
import { setRecoil } from "recoil-nexus";
import { userState } from "../../state/atoms/user";
import { loggedInState } from "../../state/atoms/loggedIn";
import { accessTokenState } from "../../state/atoms/accessToken";
import { userModelState } from "../../state/atoms/userModel";
import { userHostModelState } from "../../state/atoms/userHostModel";
import { stripeCustomerState } from "../../state/atoms/stripeCustomer";
import { stripeAccountState } from "../../state/atoms/stripeAccount";

export interface AuthError {
    code?: string;
    message?: string;
    name?: string;
}

export const handleAuthError = async (error: AuthError): Promise<boolean> => {
    console.log("Handling auth error:", error);
    
    // Check if it's a token revocation error
    const isTokenRevoked = error.code === "NotAuthorizedException" ||
                          error.message?.includes("Access Token has been revoked") ||
                          error.message?.includes("Token has been revoked") ||
                          error.message?.includes("Token has been revoked");
    
    if (isTokenRevoked) {
        console.log("Token has been revoked, performing global logout");
        
        try {
            // Sign out from Amplify
            await Auth.signOut({ global: true });
        } catch (signOutError) {
            console.log("Error during sign out:", signOutError);
        }
        
        // Clear all user state
        setRecoil(userState, null);
        setRecoil(loggedInState, false);
        setRecoil(accessTokenState, "");
        setRecoil(userModelState, null);
        setRecoil(userHostModelState, null);
        setRecoil(stripeCustomerState, null);
        setRecoil(stripeAccountState, null);
        
        // Shutdown Intercom if it exists
        if ((window as any).Intercom) {
            (window as any).Intercom("shutdown");
        }
        
        // Redirect to home page
        window.location.href = "/";
        return true; // Error was handled
    }
    
    // Check if it's a session expiration error
    const isSessionExpired = error.code === "TokenExpiredException" ||
                           error.message?.includes("Token expired") ||
                           error.message?.includes("Session expired");
    
    if (isSessionExpired) {
        console.log("Session expired, attempting to refresh");
        
        try {
            // Try to refresh the session
            await Auth.currentAuthenticatedUser();
            return false; // Session was refreshed, let the original request retry
        } catch (refreshError) {
            console.log("Session refresh failed:", refreshError);
            // If refresh fails, treat it as a logout
            return await handleAuthError({ 
                code: "NotAuthorizedException", 
                message: "Session refresh failed" 
            });
        }
    }
    
    // For other auth errors, return false to let the calling code handle it
    return false;
};

export const isAuthError = (error: any): boolean => {
    return error.code === "NotAuthorizedException" ||
           error.code === "TokenExpiredException" ||
           error.message?.includes("Access Token has been revoked") ||
           error.message?.includes("Token has been revoked") ||
           error.message?.includes("Token expired") ||
           error.message?.includes("Session expired") ||
           (error.response && (error.response.status === 401 || error.response.status === 403));
}; 